import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSocieties } from "../store/slices/societySlice";
import { editPlot, deletePlot } from "../store/slices/plotSlice";
import {
  Edit as EditIcon,
  Trash2 as DeleteIcon,
  ShieldX,
  Filter,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowDownCircle,
  ArrowUpCircle,
  AlertTriangle,
  CheckCircle2,
  Map,
  Grid2X2,
  Copy,
  Info,
  Building,
  FileBarChart,
  Printer,
} from "lucide-react";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { motion, AnimatePresence } from "framer-motion";

const PlotList = () => {
  const dispatch = useDispatch();
  const { societies } = useSelector((state) => state.societies);
  const { activeSociety, theme, gradient } = useSidebarContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "plot_number",
    direction: "ascending",
  });
  const [hoveredRow, setHoveredRow] = useState(null);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [totalBooked, setTotalBooked] = useState(0);
  const [totalHold, setTotalHold] = useState(0);
  const plotsPerPage = 10;
  const { enqueueSnackbar } = useSnackbar();

  const [filters, setFilters] = useState({
    plotNumber: "",
    block: "",
    size: "",
    marla: "",
    category: "",
    booking_status: "",
    type: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPlot, setCurrentPlot] = useState(null);
  const [showStats, setShowStats] = useState(true);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printStatusFilter, setPrintStatusFilter] = useState("All");

  useEffect(() => {
    dispatch(fetchSocieties());
  }, [dispatch]);

  useEffect(() => {
    if (societies.length > 0) {
      setCurrentPage(1);
    }
  }, [societies]);

  useEffect(() => {
    if (
      activeSociety &&
      activeSociety.plots &&
      activeSociety.plots.length > 0
    ) {
      // Calculate statistics
      setTotalAvailable(
        activeSociety.plots.filter(
          (plot) => plot.booking_status === "Available"
        ).length
      );
      setTotalBooked(
        activeSociety.plots.filter((plot) => plot.booking_status === "Booked")
          .length
      );
      setTotalHold(
        activeSociety.plots.filter((plot) => plot.booking_status === "Hold")
          .length
      );
    }
  }, [activeSociety]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "ascending" ? (
      <ArrowUpCircle size={16} className="ml-1" />
    ) : (
      <ArrowDownCircle size={16} className="ml-1" />
    );
  };

  const filteredPlots =
    activeSociety && activeSociety.plots
      ? activeSociety.plots
        .filter(
          (plot) =>
            (filters.plotNumber === "" ||
              plot.plot_number
                .toString()
                .toLowerCase()
                .includes(filters.plotNumber.toLowerCase())) &&
            (filters.block === "" ||
              plot.block
                .toLowerCase()
                .includes(filters.block.toLowerCase())) &&
            (filters.size === "" ||
              plot.size.toString().includes(filters.size)) &&
            (filters.marla === "" ||
              plot.marla.toString().includes(filters.marla)) &&
            (filters.booking_status === "" ||
              plot.booking_status
                .toString()
                .toLowerCase()
                .includes(filters.booking_status.toLowerCase())) &&
            (filters.category === "" ||
              plot.category.toLowerCase() ===
              filters.category.toLowerCase()) &&
            (filters.type === "" ||
              plot.plot_type.toLowerCase() === filters.type.toLowerCase())
        )
        .sort((a, b) => {
          if (sortConfig.key === "plot_number") {
            return sortConfig.direction === "ascending"
              ? a.plot_number.localeCompare(b.plot_number, undefined, {
                numeric: true,
              })
              : b.plot_number.localeCompare(a.plot_number, undefined, {
                numeric: true,
              });
          } else if (sortConfig.key === "size") {
            return sortConfig.direction === "ascending"
              ? parseFloat(a.size) - parseFloat(b.size)
              : parseFloat(b.size) - parseFloat(a.size);
          } else if (sortConfig.key === "marla") {
            return sortConfig.direction === "ascending"
              ? parseFloat(a.marla) - parseFloat(b.marla)
              : parseFloat(b.marla) - parseFloat(a.marla);
          } else if (sortConfig.key === "block") {
            return sortConfig.direction === "ascending"
              ? a.block.localeCompare(b.block)
              : b.block.localeCompare(a.block);
          } else if (sortConfig.key === "category") {
            return sortConfig.direction === "ascending"
              ? a.category.localeCompare(b.category)
              : b.category.localeCompare(a.category);
          } else if (sortConfig.key === "plot_type") {
            return sortConfig.direction === "ascending"
              ? a.plot_type.localeCompare(b.plot_type)
              : b.plot_type.localeCompare(a.plot_type);
          } else if (sortConfig.key === "booking_status") {
            return sortConfig.direction === "ascending"
              ? a.booking_status.localeCompare(b.booking_status)
              : b.booking_status.localeCompare(a.booking_status);
          }
          return 0;
        })
      : [];

  const indexOfLastPlot = currentPage * plotsPerPage;
  const indexOfFirstPlot = indexOfLastPlot - plotsPerPage;
  const currentPlots = filteredPlots.slice(indexOfFirstPlot, indexOfLastPlot);

  const refreshPlots = async () => {
    await dispatch(fetchSocieties());
  };

  const handlePrintRequest = () => {
    setIsPrintModalOpen(true);
  };

  const executePrint = () => {
    setIsPrintModalOpen(false);

    let plotsToPrint = [...filteredPlots];
    let filterSource = "All Plots";

    if (printStatusFilter !== "All") {
      plotsToPrint = plotsToPrint.filter(
        (plot) => plot.booking_status === printStatusFilter
      );
      filterSource = `${printStatusFilter} Plots`;
    } else {
      if (filters.booking_status === "Available") filterSource = "Available Plots";
      else if (filters.booking_status === "Booked") filterSource = "Booked Plots";
      else if (filters.booking_status === "Hold") filterSource = "Hold Plots";
    }

    if (plotsToPrint.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Plots to Print",
        text: `There are no ${printStatusFilter !== "All" ? printStatusFilter : ""} plots matching your current filters.`,
        background: `linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)`,
        color: "#5EEAD4",
        confirmButtonColor: `#3b82f6`,
      });
      return;
    }

    const getStatusStyle = (status) => {
      switch (status) {
        case "Available": return "background:#dcfce7;color:#15803d;border:1.5px solid #86efac;";
        case "Hold": return "background:#fef9c3;color:#a16207;border:1.5px solid #fde047;";
        case "Booked": return "background:#fee2e2;color:#dc2626;border:1.5px solid #fca5a5;";
        case "Transfer": return "background:#ede9fe;color:#6d28d9;border:1.5px solid #c4b5fd;";
        default: return "background:#f1f5f9;color:#475569;border:1.5px solid #cbd5e1;";
      }
    };

    const currentDate = new Date().toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

    // Status summary counts
    const counts = {
      Available: plotsToPrint.filter(p => p.booking_status === "Available").length,
      Booked: plotsToPrint.filter(p => p.booking_status === "Booked").length,
      Hold: plotsToPrint.filter(p => p.booking_status === "Hold").length,
      Transfer: plotsToPrint.filter(p => p.booking_status === "Transfer").length,
    };

    const statsCards = [
      { label: "Total", value: plotsToPrint.length, bg: "#eef2ff", text: "#4338ca", border: "#c7d2fe" },
      { label: "Available", value: counts.Available, bg: "#f0fdf4", text: "#15803d", border: "#86efac" },
      { label: "Booked", value: counts.Booked, bg: "#fef2f2", text: "#991b1b", border: "#fca5a5" },
      { label: "Hold", value: counts.Hold, bg: "#fffbeb", text: "#92400e", border: "#fde68a" },
    ].map(s => `
        <div style="background:${s.bg};border:1.5px solid ${s.border};border-radius:16px;padding:20px;text-align:center;flex:1;">
            <p style="margin:0 0 4px 0;font-size:36px;font-weight:900;color:${s.text};letter-spacing:-1.5px;line-height:1;">${s.value}</p>
            <p style="margin:0;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:1.5px;color:${s.text};opacity:0.75;">${s.label}</p>
        </div>`).join('');

    const tableRows = plotsToPrint.map((plot, index) => {
      const rowBg = index % 2 === 0 ? '#ffffff' : '#fafbff';
      return `
        <tr style="background:${rowBg};">
            <td style="padding:12px 16px;border-bottom:1px solid #f1f5f9;font-size:13px;font-weight:800;color:#0f172a;font-family:'DM Mono',monospace;">${plot.plot_number}</td>
            <td style="padding:12px 16px;border-bottom:1px solid #f1f5f9;font-size:13px;font-weight:600;color:#334155;">
                <span style="background:#f1f5f9;border:1px solid #e2e8f0;padding:3px 10px;border-radius:6px;font-size:12px;font-weight:700;color:#475569;">${plot.block}</span>
            </td>
            <td style="padding:12px 16px;border-bottom:1px solid #f1f5f9;font-size:13px;color:#334155;font-weight:600;">${plot.size}</td>
            <td style="padding:12px 16px;border-bottom:1px solid #f1f5f9;font-size:13px;color:#334155;font-family:'DM Mono',monospace;font-weight:600;">${plot.marla}</td>
            <td style="padding:12px 16px;border-bottom:1px solid #f1f5f9;font-size:13px;color:#334155;">${plot.category}</td>
            <td style="padding:12px 16px;border-bottom:1px solid #f1f5f9;font-size:13px;color:#334155;">${plot.plot_type}</td>
            <td style="padding:12px 16px;border-bottom:1px solid #f1f5f9;text-align:center;">
                <span style="${getStatusStyle(plot.booking_status)} font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.8px;padding:5px 14px;border-radius:100px;display:inline-block;white-space:nowrap;">
                    ${plot.booking_status}
                </span>
            </td>
        </tr>`;
    }).join('');

    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <title>Plot List - ${activeSociety?.name || 'Society'}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
            /* First page: no margin so our full-bleed header fills edge-to-edge */
            @page :first {
                size: A4 portrait;
                margin: 0mm;
            }
            /* All continuation pages: generous margins so table never bleeds to edge */
            @page {
                size: A4 portrait;
                margin: 16mm 14mm 22mm 14mm;
            }
            *, *::before, *::after {
                box-sizing: border-box;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
            html, body {
                margin: 0; padding: 0;
                font-family: 'Plus Jakarta Sans', sans-serif;
                background: #f1f5f9;
                color: #0f172a;
            }
            /* Prevent rows from splitting across page breaks */
            tr { page-break-inside: avoid; }
            /* Add breathing room before a page break happens inside the table */
            tbody tr:last-child td { padding-bottom: 18px; }
        </style>
    </head>
    <body>
    <div style="font-family:'Plus Jakarta Sans',sans-serif;background:#f1f5f9;color:#0f172a;margin:0;padding:0;">

        <!-- TOP RAINBOW BAR -->
        <div style="height:6px;background:linear-gradient(90deg,#6366f1 0%,#8b5cf6 30%,#ec4899 60%,#f59e0b 100%);"></div>

        <!-- DARK HEADER -->
        <div style="background:#0f172a;padding:44px 56px 40px;position:relative;overflow:hidden;">
            <div style="position:absolute;top:-70px;right:-70px;width:260px;height:260px;border-radius:50%;background:radial-gradient(circle,rgba(99,102,241,0.22) 0%,transparent 70%);"></div>
            <div style="position:absolute;bottom:-50px;left:160px;width:180px;height:180px;border-radius:50%;background:radial-gradient(circle,rgba(236,72,153,0.16) 0%,transparent 70%);"></div>
            <div style="position:absolute;top:20px;right:260px;width:100px;height:100px;border-radius:50%;background:radial-gradient(circle,rgba(251,191,36,0.13) 0%,transparent 70%);"></div>

            <div style="position:relative;z-index:2;display:flex;justify-content:space-between;align-items:center;">
                <div>
                    <div style="display:inline-flex;align-items:center;gap:8px;background:rgba(99,102,241,0.18);border:1px solid rgba(99,102,241,0.38);border-radius:100px;padding:7px 18px;margin-bottom:18px;">
                        <div style="width:6px;height:6px;border-radius:50%;background:#818cf8;"></div>
                        <span style="font-size:10px;font-weight:800;letter-spacing:2.5px;text-transform:uppercase;color:#a5b4fc;">AL Noor Developers · Official Record</span>
                    </div>
                    <h1 style="margin:0 0 6px 0;font-size:44px;font-weight:900;color:#ffffff;letter-spacing:-2px;line-height:1.05;">${activeSociety?.name || 'Society'}</h1>
                    <p style="margin:0;font-size:16px;color:#94a3b8;font-weight:400;">Plot Inventory Report &nbsp;·&nbsp; ${filterSource}</p>
                </div>
                <div style="text-align:right;background:rgba(255,255,255,0.07);border:1px solid rgba(255,255,255,0.1);border-radius:18px;padding:22px 28px;flex-shrink:0;">
                    <p style="margin:0 0 5px 0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2.5px;color:#64748b;">Generated On</p>
                    <p style="margin:0;font-size:13px;font-weight:700;color:#e2e8f0;font-family:'DM Mono',monospace;">${currentDate}</p>
                </div>
            </div>
        </div>

        <!-- CONTENT AREA -->
        <div style="padding:40px 56px 60px;">

            <!-- STATS ROW -->
            <div style="display:flex;gap:16px;margin-bottom:28px;">
                ${statsCards}
            </div>

            <!-- FILTER INFO STRIP -->
            <div style="background:#ffffff;border-radius:14px;border:1px solid #e2e8f0;padding:16px 24px;margin-bottom:24px;display:flex;align-items:center;justify-content:space-between;box-shadow:0 2px 8px rgba(0,0,0,0.04);">
                <div style="display:flex;align-items:center;gap:10px;">
                    <div style="width:8px;height:8px;border-radius:50%;background:linear-gradient(135deg,#6366f1,#ec4899);"></div>
                    <span style="font-size:13px;font-weight:700;color:#0f172a;">Active Filter: </span>
                    <span style="font-size:13px;font-weight:600;color:#6366f1;">${filterSource}</span>
                </div>
                <span style="font-size:12px;color:#94a3b8;font-family:'DM Mono',monospace;font-weight:600;">${plotsToPrint.length} plots listed below</span>
            </div>

            <!-- TABLE SECTION HEADER -->
            <div style="display:flex;align-items:center;gap:14px;margin-bottom:14px;">
                <div style="width:4px;height:24px;border-radius:3px;background:linear-gradient(180deg,#6366f1,#ec4899);flex-shrink:0;"></div>
                <span style="font-size:16px;font-weight:800;color:#0f172a;letter-spacing:-0.3px;">Plot Inventory</span>
                <div style="flex:1;height:1.5px;background:linear-gradient(90deg,#e2e8f0,transparent);"></div>
            </div>

            <!-- TABLE -->
            <div style="background:#ffffff;border-radius:18px;border:1px solid #e2e8f0;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.05);margin-bottom:32px;">
                <table style="width:100%;border-collapse:collapse;text-align:left;">
                    <thead style="display:table-header-group;">
                        <tr style="background:linear-gradient(90deg,#1e293b 0%,#0f172a 100%);">
                            <th style="padding:15px 16px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;white-space:nowrap;">Plot #</th>
                            <th style="padding:15px 16px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;">Block</th>
                            <th style="padding:15px 16px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;">Size</th>
                            <th style="padding:15px 16px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;">Marla</th>
                            <th style="padding:15px 16px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;">Category</th>
                            <th style="padding:15px 16px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;">Type</th>
                            <th style="padding:15px 16px;font-size:10px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:1.5px;text-align:center;">Status</th>
                        </tr>
                    </thead>
                    <tbody>${tableRows}</tbody>
                </table>
            </div>

            <!-- FOOTER -->
            <div style="margin-top:32px;padding:20px 28px;background:#ffffff;border-radius:16px;border:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center;box-shadow:0 2px 10px rgba(0,0,0,0.04);">
                <div style="display:flex;align-items:center;gap:14px;">
                    <div style="width:38px;height:38px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 4px 12px rgba(16,185,129,0.28);">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <div>
                        <p style="margin:0 0 2px 0;font-size:13px;font-weight:800;color:#0f172a;">End of verified plot records.</p>
                        <p style="margin:0;font-size:11px;color:#94a3b8;font-family:'DM Mono',monospace;">Generated by ALD CRM System &copy; ${new Date().getFullYear()} · Do not alter</p>
                    </div>
                </div>
                <div style="text-align:right;">
                    <p style="margin:0 0 2px 0;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#cbd5e1;">Total Plots</p>
                    <p style="margin:0;font-size:34px;font-weight:900;color:#e2e8f0;letter-spacing:-1.5px;line-height:1;">${plotsToPrint.length}</p>
                </div>
            </div>

        </div>

        <!-- BOTTOM RAINBOW BAR -->
        <div style="height:5px;background:linear-gradient(90deg,#f59e0b 0%,#ec4899 35%,#8b5cf6 65%,#6366f1 100%);"></div>

    </div>
    </body>
    </html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(printContent);
    doc.close();

    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      plotNumber: "",
      block: "",
      size: "",
      marla: "",
      category: "",
      booking_status: "",
      type: "",
    });
    setCurrentPage(1);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setCurrentPlot(null);
  };

  const openEditModal = (plot) => {
    if (!plot) {
      console.error("Invalid plot passed into openEditModal");
      return;
    }

    setCurrentPlot(plot);
    setIsModalOpen(true);
  };

  const handleDelete = async (plotId, currentStatus) => {
    if (!activeSociety || !activeSociety._id) {
      console.error("Society data is missing or invalid");
      alert("Please select a society before proceeding.");
      return;
    }

    if (currentStatus === "Available") {
      // Logic for deleting a plot
      Swal.fire({
        title: "Delete Plot?",
        text: "Are you sure you want to delete this plot permanently?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete!",
        cancelButtonText: "No, cancel!",
        background: `linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)`,
        color: "#5EEAD4",
        confirmButtonColor: `#EF4444`,
        cancelButtonColor: `#64748b`,
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(deletePlot({ societyId: activeSociety._id, plotId }))
            .then(() => {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "The plot has been deleted.",
                background: `linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)`,
                color: `#5EEAD4`,
                confirmButtonColor: `#008000`,
              });
            })
            .catch((error) => {
              Swal.fire({
                icon: "error",
                title: "Error!",
                text: "Failed to delete the plot.",
                background: `linear-gradient(to bottom right, #1e293b, #0f172a, #1e293b)`,
                color: `#5EEAD4`,
                confirmButtonColor: `#f27474`,
              });
            });
        }
      });
    } else {
      alert("Booked plots cannot be deleted.");
    }
    await refreshPlots();
  };

  const handleEdit = async (updatedPlotData) => {
    if (!currentPlot) {
      console.error("currentPlot is null or undefined");
      return;
    }

    const duplicatePlot = activeSociety.plots.find(
      (plot) =>
        plot.plot_number === updatedPlotData.plot_number &&
        plot._id !== currentPlot._id
    );

    if (duplicatePlot) {
      setErrorMessage(
        `Plot number "${duplicatePlot.plot_number}" already exists in "${activeSociety.name}"! Please choose a different one.`
      );
      return;
    }
    setErrorMessage("");

    const plotData = {
      societyId: activeSociety._id,
      plotId: currentPlot._id,
      plotData: updatedPlotData,
    };

    dispatch(editPlot(plotData))
      .then(() => {
        closeEditModal();
        enqueueSnackbar("Plot updated successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      })
      .catch((error) => {
        console.error("Error editing plot:", error);
        enqueueSnackbar("An error occurred while editing the plot.", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      });
    await refreshPlots();
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(filteredPlots.length / plotsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (!activeSociety) {
    return (
      <div
        className={`${theme.tailwind.bg14} ${theme.tailwind.text9} p-8 rounded-xl shadow-xl`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto text-center py-20"
        >
          <Building
            size={80}
            className={`mx-auto mb-8 ${theme.tailwind.text} opacity-80`}
          />
          <h2 className={`text-5xl font-bold ${theme.tailwind.text9} mb-6`}>
            Plot Management System
          </h2>
          <p className={`text-2xl ${theme.tailwind.text} max-w-2xl mx-auto`}>
            Please select a society from the sidebar to view and manage plots.
          </p>
          <div className="mt-12 flex justify-center">
            <div
              className={`px-6 py-4 ${theme.tailwind.bgr33} rounded-xl border ${theme.tailwind.border11} backdrop-blur-sm`}
            >
              <Map
                size={24}
                className={`inline-block mr-2 ${theme.tailwind.text}`}
              />
              <span className={`${theme.tailwind.text9}`}>
                Select a society to get started
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`container mx-auto max-w-7xl space-y-6 md:space-y-8 ${gradient} rounded-3xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] overflow-hidden border ${theme.tailwind.border2}`}
      >
        {/* Header Section */}
        <div className="relative overflow-hidden">
          <div className={`absolute inset-0 ${theme.tailwind.bgr34} z-0`}></div>
          <div
            className={`absolute -right-20 -top-20 w-64 h-64 ${theme.tailwind.bg25} rounded-full filter blur-3xl`}
          ></div>
          <div
            className={`absolute -left-20 -bottom-20 w-64 h-64 ${theme.tailwind.bg25} rounded-full filter blur-3xl`}
          ></div>

          <div className="relative z-10 flex flex-col md:flex-row md:justify-between md:items-center p-6 md:p-10 py-8 md:py-14">
            <div className="space-y-4 mb-6 md:mb-0">
              <div className="flex flex-wrap items-center gap-4">
                <h2
                  className={`text-4xl md:text-5xl font-bold ${theme.tailwind.text9} tracking-tight`}
                >
                  Plot Management
                </h2>
                {activeSociety && (
                  <div
                    className={`px-4 py-1.5 ${theme.tailwind.bg61} ${theme.tailwind.text9} rounded-full text-sm font-medium border ${theme.tailwind.border15} backdrop-blur-sm`}
                  >
                    {activeSociety.name}
                  </div>
                )}
              </div>
              <p
                className={`${theme.tailwind.text} text-lg md:text-xl max-w-xl leading-relaxed`}
              >
                Seamlessly manage and track your society's plots with intuitive
                precision
              </p>
            </div>

            {/* Filters Toggle Button */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handlePrintRequest}
                className={`group relative flex items-center gap-2 px-5 py-2.5 
                  ${theme.tailwind.bg10} ${theme.tailwind.text9} 
                  rounded-xl 
                  ${theme.tailwind.bgHover3} 
                  transition-all duration-300 
                  transform hover:scale-105 
                  hover:shadow-lg border ${theme.tailwind.border15}
                  ${theme.tailwind.hoverB8}`}
              >
                <span className="relative z-10">
                  <Printer size={20} />
                </span>
                <span className="relative z-10 font-medium">
                  Print
                </span>
              </button>

              <button
                onClick={() => setShowStats(!showStats)}
                className={`group relative flex items-center gap-2 px-5 py-2.5 
                  ${theme.tailwind.bg10} ${theme.tailwind.text9} 
                  rounded-xl 
                  ${theme.tailwind.bgHover3} 
                  transition-all duration-300 
                  transform hover:scale-105 
                  hover:shadow-lg border ${theme.tailwind.border15}
                  ${theme.tailwind.hoverB8}`}
              >
                <span className="relative z-10">
                  <Info size={20} />
                </span>
                <span className="relative z-10 font-medium">
                  {showStats ? "Hide Stats" : "Show Stats"}
                </span>
              </button>

              <button
                onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                className={`group relative flex items-center gap-2 px-5 py-2.5 
                  ${theme.tailwind.bg10} ${theme.tailwind.text9} 
                  rounded-xl 
                  ${theme.tailwind.bgHover3} 
                  transition-all duration-300 
                  transform hover:scale-105 
                  hover:shadow-lg border ${theme.tailwind.border15}
                  ${theme.tailwind.hoverB8}`}
              >
                <span className="relative z-10">
                  {isFiltersExpanded ? <X size={20} /> : <Filter size={20} />}
                </span>
                <span className="relative z-10 font-medium">
                  {isFiltersExpanded ? "Close Filters" : "Filter Plots"}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="px-6 overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Plots Card */}
                <div
                  className={`${theme.tailwind.bg8} rounded-2xl p-5 border ${theme.tailwind.border2} shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-2px]`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p
                        className={`${theme.tailwind.text} text-sm font-medium`}
                      >
                        Total Plots
                      </p>
                      <h3 className="text-3xl font-bold text-white mt-1">
                        {filteredPlots.length}
                      </h3>
                    </div>
                    <div className={`${theme.tailwind.bg25} p-3 rounded-xl`}>
                      <Grid2X2 className={`h-6 w-6 ${theme.tailwind.text}`} />
                    </div>
                  </div>
                  <div
                    className={`mt-4 flex items-center text-xs ${theme.tailwind.text9}`}
                  >
                    <FileBarChart className="h-4 w-4 mr-1" />
                    <span>All plots in {activeSociety.name}</span>
                  </div>
                </div>

                {/* Available Plots */}
                <div
                  className={`${theme.tailwind.bg8} rounded-2xl p-5 border ${theme.tailwind.border2} shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-2px]`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-green-400 text-sm font-medium">
                        Available Plots
                      </p>
                      <h3 className="text-3xl font-bold text-white mt-1">
                        {totalAvailable}
                      </h3>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded-xl">
                      <CheckCircle2 className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-xs text-green-300">
                    <span className="inline-block h-2 w-2 rounded-full bg-green-400 mr-2"></span>
                    <span>Ready for booking</span>
                  </div>
                </div>

                {/* Booked Plots */}
                <div
                  className={`${theme.tailwind.bg8} rounded-2xl p-5 border ${theme.tailwind.border2} shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-2px]`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-pink-400 text-sm font-medium`}>
                        Booked Plots
                      </p>
                      <h3 className="text-3xl font-bold text-white mt-1">
                        {totalBooked}
                      </h3>
                    </div>
                    <div className="bg-pink-500/10 p-3 rounded-xl">
                      <Copy className={`h-6 w-6 text-pink-400`} />
                    </div>
                  </div>
                  <div
                    className={`mt-4 flex items-center text-xs text-pink-300`}
                  >
                    <span
                      className={`inline-block h-2 w-2 rounded-full bg-pink-400 mr-2`}
                    ></span>
                    <span>Currently booked</span>
                  </div>
                </div>

                {/* Booking Rate */}
                <div
                  className={`${theme.tailwind.bg8} rounded-2xl p-5 border ${theme.tailwind.border2} shadow-lg hover:shadow-xl transition-all duration-300 hover:translate-y-[-2px]`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-orange-400 text-sm font-medium`}>
                        Booking Rate
                      </p>
                      <h3 className="text-3xl font-bold text-white mt-1">
                        {filteredPlots.length > 0
                          ? Math.round(
                            (totalBooked / filteredPlots.length) * 100
                          )
                          : 0}
                        %
                      </h3>
                    </div>
                    <div className={`${theme.tailwind.bg25} p-3 rounded-xl`}>
                      <AlertTriangle className={`h-6 w-6 text-orange-400`} />
                    </div>
                  </div>
                  <div className="mt-4">
                    <div
                      className={`w-full ${theme.tailwind.bg5} rounded-full h-2.5`}
                    >
                      <div
                        className={`bg-orange-400 h-2.5 rounded-full`}
                        style={{
                          width: `${filteredPlots.length > 0
                            ? Math.round(
                              (totalBooked / filteredPlots.length) * 100
                            )
                            : 0
                            }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filters Section */}
        <AnimatePresence>
          {isFiltersExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="px-6 overflow-hidden"
            >
              <div
                className={`${theme.tailwind.bg32} p-6 rounded-xl border border-slate-700/80 backdrop-blur-sm mb-6 relative overflow-hidden`}
              >
                <div
                  className={`absolute inset-0 ${theme.tailwind.bgr35} z-0`}
                ></div>
                <div className="relative z-10">
                  <div className="flex justify-between items-center mb-6">
                    <h3
                      className={`text-xl font-semibold ${theme.tailwind.text9} flex items-center`}
                    >
                      <Filter size={18} className="mr-2" />
                      Filter Plots
                    </h3>
                    <button
                      onClick={resetFilters}
                      className={`px-4 py-1.5 ${theme.tailwind.bg47} ${theme.tailwind.text9} rounded-lg text-sm ${theme.tailwind.bgHover24} transition-colors`}
                    >
                      Reset Filters
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {/* Filter inputs */}
                    <div className="relative">
                      <label
                        className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                      >
                        Plot Number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="plotNumber"
                          value={filters.plotNumber}
                          onChange={handleFilterChange}
                          placeholder="Search Plot Number"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder5}`}
                        />
                        <Search
                          className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                          size={18}
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label
                        className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                      >
                        Plot Block
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="block"
                          value={filters.block}
                          onChange={handleFilterChange}
                          placeholder="Search Block"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder5}`}
                        />
                        <Search
                          className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                          size={18}
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label
                        className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                      >
                        Plot Size
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="size"
                          value={filters.size}
                          onChange={handleFilterChange}
                          placeholder="Search Size"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder5}`}
                        />
                        <Search
                          className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                          size={18}
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label
                        className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                      >
                        Plot Marla
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="marla"
                          value={filters.marla}
                          onChange={handleFilterChange}
                          placeholder="Search Marla"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} ${theme.tailwind.placeHolder5}`}
                        />
                        <Search
                          className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.tailwind.text}`}
                          size={18}
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <label
                        className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                      >
                        Category
                      </label>
                      <select
                        name="category"
                        value={filters.category}
                        onChange={handleFilterChange}
                        className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="">All Categories</option>
                        <option value="Residential">Residential</option>
                        <option value="Commercial">Commercial</option>
                      </select>
                    </div>
                    <div className="relative">
                      <label
                        className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                      >
                        Plot Type
                      </label>
                      <select
                        name="type"
                        value={filters.type}
                        onChange={handleFilterChange}
                        className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="">All Types</option>
                        <option value="Corner">Corner</option>
                        <option value="Normal">Normal</option>
                      </select>
                    </div>
                    <div className="relative">
                      <label
                        className={`block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                      >
                        Status
                      </label>
                      <select
                        name="booking_status"
                        value={filters.booking_status}
                        onChange={handleFilterChange}
                        className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="">All Statuses</option>
                        <option value="Available">Available</option>
                        <option value="Hold">Hold</option>
                        <option value="Transfer">Transfer</option>
                        <option value="Booked">Booked</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Table Section */}
        <div className="px-6 pb-6">
          <div
            className={`${theme.tailwind.bg16} rounded-xl border ${theme.tailwind.border2} overflow-hidden shadow-xl`}
          >
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className={`${theme.tailwind.bg55} ${theme.tailwind.text9} border-b ${theme.tailwind.border2}`}
                  >
                    <th
                      className={`px-4 py-4 text-left font-medium cursor-pointer ${theme.tailwind.bgHover16}`}
                      onClick={() => requestSort("plot_number")}
                    >
                      <div className="flex items-center">
                        Plot # {getSortIndicator("plot_number")}
                      </div>
                    </th>
                    <th
                      className={`px-4 py-4 text-left font-medium cursor-pointer ${theme.tailwind.bgHover16}`}
                      onClick={() => requestSort("block")}
                    >
                      <div className="flex items-center">
                        Block {getSortIndicator("block")}
                      </div>
                    </th>
                    <th
                      className={`px-4 py-4 text-left font-medium cursor-pointer ${theme.tailwind.bgHover16}`}
                      onClick={() => requestSort("size")}
                    >
                      <div className="flex items-center">
                        Size {getSortIndicator("size")}
                      </div>
                    </th>
                    <th
                      className={`px-4 py-4 text-left font-medium cursor-pointer ${theme.tailwind.bgHover16}`}
                      onClick={() => requestSort("marla")}
                    >
                      <div className="flex items-center">
                        Marla {getSortIndicator("marla")}
                      </div>
                    </th>
                    <th
                      className={`px-4 py-4 text-left font-medium cursor-pointer ${theme.tailwind.bgHover16}`}
                      onClick={() => requestSort("category")}
                    >
                      <div className="flex items-center">
                        Category {getSortIndicator("category")}
                      </div>
                    </th>
                    <th
                      className={`px-4 py-4 text-left font-medium cursor-pointer ${theme.tailwind.bgHover16}`}
                      onClick={() => requestSort("plot_type")}
                    >
                      <div className="flex items-center">
                        Type {getSortIndicator("plot_type")}
                      </div>
                    </th>
                    <th
                      className={`px-4 py-4 text-left font-medium cursor-pointer ${theme.tailwind.bgHover16}`}
                      onClick={() => requestSort("booking_status")}
                    >
                      <div className="flex items-center">
                        Status {getSortIndicator("booking_status")}
                      </div>
                    </th>
                    <th className="px-4 py-4 text-center font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${theme.tailwind.divide}`}>
                  {currentPlots.length > 0 ? (
                    currentPlots.map((plot, index) => (
                      <tr
                        key={plot._id || index}
                        className={`group transition-colors duration-150 ${theme.tailwind.bgHover14
                          } ${hoveredRow === plot._id ? `${gradient}` : ""}`}
                        onMouseEnter={() => setHoveredRow(plot._id)}
                        onMouseLeave={() => setHoveredRow(null)}
                      >
                        <td className="px-4 py-3.5 text-white font-medium">
                          {plot.plot_number}
                        </td>
                        <td
                          className={`px-4 py-3.5 ${theme.tailwind.texting1}`}
                        >
                          {plot.block}
                        </td>
                        <td
                          className={`px-4 py-3.5 ${theme.tailwind.texting1}`}
                        >
                          {plot.size}
                        </td>
                        <td
                          className={`px-4 py-3.5 ${theme.tailwind.texting1}`}
                        >
                          {plot.marla}
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${plot.category === "Residential"
                              ? "bg-blue-500/10 text-blue-400"
                              : "bg-purple-500/10 text-purple-400"
                              }`}
                          >
                            {plot.category}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${plot.plot_type === "Corner"
                              ? "bg-amber-500/10 text-amber-400"
                              : `${theme.tailwind.bg25} ${theme.tailwind.text}`
                              }`}
                          >
                            {plot.plot_type}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${plot.booking_status === "Available"
                              ? "bg-green-500/10 text-green-400"
                              : plot.booking_status === "Hold"
                                ? "bg-yellow-500/10 text-yellow-400"
                                : plot.booking_status === "Transfer"
                                  ? "bg-gray-500/10 text-gray-400"
                                  : "bg-red-500/10 text-red-400"
                              }`}
                          >
                            {plot.booking_status}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          <div className="flex justify-center space-x-2">
                            <button
                              onClick={() => openEditModal(plot)}
                              className="p-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors duration-200"
                              title="Edit Plot"
                            >
                              <EditIcon size={16} />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete(plot._id, plot.booking_status)
                              }
                              className={`p-1.5 ${plot.booking_status === "Available"
                                ? "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                : "bg-gray-500/10 text-gray-400 cursor-not-allowed"
                                } rounded-lg transition-colors duration-200`}
                              title="Delete Plot"
                              disabled={plot.booking_status !== "Available"}
                            >
                              <DeleteIcon size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className={`px-4 py-8 text-center ${theme.tailwind.texting2}`}
                      >
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <AlertTriangle
                            size={32}
                            className="text-yellow-500 opacity-70"
                          />
                          <p className="text-lg font-medium">
                            No plots found matching your filters
                          </p>
                          <button
                            onClick={resetFilters}
                            className={`px-4 py-2 ${theme.tailwind.bg62} ${theme.tailwind.text} rounded-lg text-sm ${theme.tailwind.bgHover3} transition-colors`}
                          >
                            Reset Filters
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredPlots.length > 0 && (
              <div
                className={`relative px-6 py-6 ${theme.tailwind.bgr36} border-t ${theme.tailwind.border3} rounded-b-xl lg:h-32`}
              >
                {/* Glowing Bar Indicator */}
                <div
                  className={`absolute top-0 left-0 right-0 h-0.5 ${theme.tailwind.bgr37} opacity-70 shadow-lg ${theme.tailwind.shadow5}`}
                ></div>

                <div className="flex flex-col sm:flex-row items-center justify-between">
                  {/* Stats with animated counter */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`text-sm ${theme.tailwind.texting2} ${theme.tailwind.bg55} backdrop-blur-sm px-4 py-2 rounded-full border ${theme.tailwind.border2} shadow-inner ${theme.tailwind.shadow6} mb-3 sm:mb-0`}
                  >
                    <span className={`font-mono ${theme.tailwind.text}`}>
                      {indexOfFirstPlot + 1}-
                      {indexOfLastPlot > filteredPlots.length
                        ? filteredPlots.length
                        : indexOfLastPlot}
                    </span>{" "}
                    <span className={`${theme.tailwind.text18}`}>of</span>{" "}
                    <span className={`font-mono ${theme.tailwind.text}`}>
                      {filteredPlots.length}
                    </span>{" "}
                    <span className={`${theme.tailwind.text18}`}>plots</span>
                  </motion.div>

                  {/* Pagination Controls */}
                  <div
                    className={`flex items-center space-x-1 backdrop-blur-sm rounded-full p-1 ${theme.tailwind.bg2} border ${theme.tailwind.border2}`}
                  >
                    {/* Previous Button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`relative group flex items-center justify-center h-9 w-9 rounded-full ${currentPage === 1
                        ? `${theme.tailwind.bg60} cursor-not-allowed opacity-50`
                        : `${theme.tailwind.bg3} ${theme.tailwind.bgHover24} hover:text-white transition-all duration-200`
                        }`}
                    >
                      <ChevronLeft
                        size={18}
                        className={`${theme.tailwind.text9} ${theme.tailwind.groupHover3}`}
                      />
                      {currentPage !== 1 && (
                        <span
                          className={`absolute -top-8 left-1/2 transform -translate-x-1/2 ${theme.tailwind.bg14} ${theme.tailwind.text9} text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"`}
                        >
                          Page {currentPage - 1}
                        </span>
                      )}
                    </motion.button>

                    <div className="hidden sm:flex items-center">
                      {(() => {
                        const totalPages = Math.ceil(
                          filteredPlots.length / plotsPerPage
                        );
                        let pages = [];
                        if (totalPages <= 5) {
                          pages = Array.from(
                            { length: totalPages },
                            (_, i) => i + 1
                          );
                        } else {
                          pages.push(1);
                          if (currentPage > 3) {
                            pages.push("...");
                          }
                          const start = Math.max(2, currentPage - 1);
                          const end = Math.min(totalPages - 1, currentPage + 1);

                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }
                          if (currentPage < totalPages - 2) {
                            pages.push("...");
                          }
                          if (totalPages > 1) {
                            pages.push(totalPages);
                          }
                        }

                        return pages.map((pageNum, index) =>
                          pageNum === "..." ? (
                            <div
                              key={`ellipsis-${index}`}
                              className={`w-8 text-center ${theme.tailwind.text18}`}
                            >
                              ⋯
                            </div>
                          ) : (
                            <motion.button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className={`relative group flex items-center justify-center h-8 w-8 rounded-full text-xs font-medium transition-all duration-300 ${pageNum === currentPage
                                ? `${theme.tailwind.bgGradient21} text-white shadow-lg ring-2 ${theme.tailwind.ringColor2}`
                                : `${theme.tailwind.bg3} ${theme.tailwind.text9} ${theme.tailwind.bgHover24} hover:text-white`
                                }`}
                            >
                              {pageNum}
                              <AnimatePresence>
                                {pageNum === currentPage && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    exit={{ scale: 0 }}
                                    className={`absolute -z-10 inset-0 rounded-full ${theme.tailwind.bg58} blur-sm`}
                                  />
                                )}
                              </AnimatePresence>
                            </motion.button>
                          )
                        );
                      })()}
                    </div>

                    {/* Next Button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleNextPage}
                      disabled={
                        currentPage ===
                        Math.ceil(filteredPlots.length / plotsPerPage)
                      }
                      className={`relative group flex items-center justify-center h-9 w-9 rounded-full ${currentPage ===
                        Math.ceil(filteredPlots.length / plotsPerPage)
                        ? `${theme.tailwind.bg60} cursor-not-allowed opacity-50`
                        : `${theme.tailwind.bg3} ${theme.tailwind.bgHover24} hover:text-white transition-all duration-200`
                        }`}
                    >
                      <ChevronRight
                        size={18}
                        className={`${theme.tailwind.text9} ${theme.tailwind.groupHover3}`}
                      />
                      {currentPage !==
                        Math.ceil(filteredPlots.length / plotsPerPage) && (
                          <span
                            className={`absolute -top-8 left-1/2 transform -translate-x-1/2 ${theme.tailwind.bg14} ${theme.tailwind.text9} text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"`}
                          >
                            Page {currentPage + 1}
                          </span>
                        )}
                    </motion.button>

                    {/* Mobile Page Indicator */}
                    <div
                      className={`sm:hidden flex items-center justify-center ${theme.tailwind.bg32} px-3 rounded-full h-8`}
                    >
                      <span
                        className={`${theme.tailwind.text} text-xs font-mono`}
                      >
                        {currentPage}{" "}
                        <span className={`${theme.tailwind.text18}`}>of</span>{" "}
                        {Math.ceil(filteredPlots.length / plotsPerPage)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Page Jump on larger screens */}
                <div className="hidden lg:flex absolute right-6 py-3">
                  <div
                    className={`flex items-center space-x-2 ${theme.tailwind.bg15} backdrop-blur-sm py-1 px-3 rounded-full border ${theme.tailwind.border3} text-xs`}
                  >
                    <span className={`${theme.tailwind.texting2}`}>
                      Jump to:
                    </span>
                    <select
                      value={currentPage}
                      onChange={(e) => setCurrentPage(Number(e.target.value))}
                      className={`${theme.tailwind.bg48} ${theme.tailwind.text9} rounded-md px-2 py-1 border-none focus:ring-1 ${theme.tailwind.focusRing2} focus:outline-none`}
                    >
                      {Array.from(
                        {
                          length: Math.ceil(
                            filteredPlots.length / plotsPerPage
                          ),
                        },
                        (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            Page {i + 1}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Edit Modal */}
      {isModalOpen && currentPlot && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm transition-opacity"
              aria-hidden="true"
              onClick={closeEditModal}
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div
              className={`inline-block align-bottom ${theme.tailwind.bg8} rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border ${theme.tailwind.border2}`}
            >
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className={`${theme.tailwind.bg3} rounded-md ${theme.tailwind.texting2} hover:text-white focus:outline-none`}
                  onClick={closeEditModal}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3
                    className={`text-2xl leading-6 font-bold ${theme.tailwind.text9} mb-6`}
                  >
                    Edit Plot
                  </h3>
                  {errorMessage && (
                    <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
                      {errorMessage}
                    </div>
                  )}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleEdit({
                        ...currentPlot,
                        plot_number: e.target.plot_number.value,
                        block: e.target.block.value,
                        size: e.target.size.value,
                        marla: e.target.marla.value,
                        category: e.target.category.value,
                        plot_type: e.target.plot_type.value,
                        booking_status: e.target.booking_status.value,
                      });
                    }}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="plot_number"
                          className={`block text-sm font-medium ${theme.tailwind.text9} mb-1`}
                        >
                          Plot Number
                        </label>
                        <input
                          type="text"
                          name="plot_number"
                          id="plot_number"
                          required
                          defaultValue={currentPlot.plot_number}
                          className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="block"
                          className={`block text-sm font-medium ${theme.tailwind.text9} mb-1`}
                        >
                          Block
                        </label>
                        <input
                          type="text"
                          name="block"
                          id="block"
                          required
                          defaultValue={currentPlot.block}
                          className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="size"
                          className={`block text-sm font-medium ${theme.tailwind.text9} mb-1`}
                        >
                          Size
                        </label>
                        <input
                          type="number"
                          name="size"
                          id="size"
                          required
                          defaultValue={currentPlot.size}
                          className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="marla"
                          className={`block text-sm font-medium ${theme.tailwind.text9} mb-1`}
                        >
                          Marla
                        </label>
                        <input
                          type="number"
                          name="marla"
                          id="marla"
                          required
                          defaultValue={currentPlot.marla}
                          className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="category"
                          className={`block text-sm font-medium ${theme.tailwind.text9} mb-1`}
                        >
                          Category
                        </label>
                        <select
                          name="category"
                          id="category"
                          required
                          defaultValue={currentPlot.category}
                          className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                        >
                          <option value="Residential">Residential</option>
                          <option value="Commercial">Commercial</option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="plot_type"
                          className={`block text-sm font-medium ${theme.tailwind.text9} mb-1`}
                        >
                          Plot Type
                        </label>
                        <select
                          name="plot_type"
                          id="plot_type"
                          required
                          defaultValue={currentPlot.plot_type}
                          className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                        >
                          <option value="Normal">Normal</option>
                          <option value="Corner">Corner</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="booking_status"
                        className={`block text-sm font-medium ${theme.tailwind.text9} mb-1`}
                      >
                        Booking Status
                      </label>
                      <select
                        name="booking_status"
                        id="booking_status"
                        required
                        defaultValue={currentPlot.booking_status}
                        className={`w-full px-4 py-2.5 rounded-lg ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9}`}
                      >
                        <option value="Available">Available</option>
                        <option value="Hold">Hold</option>
                        <option value="Transfer">Transfer</option>
                        <option value="Booked">Booked</option>
                      </select>
                    </div>
                    <div className="pt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={closeEditModal}
                        className={`px-5 py-2.5 ${theme.tailwind.bg5} ${theme.tailwind.text9} rounded-lg ${theme.tailwind.bgHover24} transition-colors`}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`px-5 py-2.5 ${theme.tailwind.bg18} text-white rounded-lg ${theme.tailwind.bgHover25} transition-colors`}
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Selection Modal */}
      {isPrintModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm transition-opacity"
              aria-hidden="true"
              onClick={() => setIsPrintModalOpen(false)}
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div
              className={`inline-block align-bottom ${theme.tailwind.bg8} rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6 border ${theme.tailwind.border2}`}
            >
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className={`${theme.tailwind.bg3} rounded-md ${theme.tailwind.texting2} hover:text-white focus:outline-none`}
                  onClick={() => setIsPrintModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3
                    className={`text-2xl leading-6 font-bold ${theme.tailwind.text9} mb-2`}
                  >
                    Print Plot List
                  </h3>
                  <p className={`text-sm ${theme.tailwind.text} mb-6`}>
                    Select which plots you would like to print from the current
                    list.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text9} mb-2`}
                      >
                        Select Plot Type to Print
                      </label>
                      <select
                        value={printStatusFilter}
                        onChange={(e) => setPrintStatusFilter(e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl ${theme.tailwind.bg47} border ${theme.tailwind.border21} ${theme.tailwind.focus3} focus:ring-2 ${theme.tailwind.focusRing1} ${theme.tailwind.text9} shadow-sm`}
                      >
                        <option value="All">All Plots (Current Filter)</option>
                        <option value="Available">Available Plots Only</option>
                        <option value="Booked">Booked Plots Only</option>
                        <option value="Hold">Hold Plots Only</option>
                        <option value="Transfer">Transfer Plots Only</option>
                      </select>
                    </div>

                    <div className="pt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setIsPrintModalOpen(false)}
                        className={`px-5 py-2.5 ${theme.tailwind.bg5} ${theme.tailwind.text9} rounded-xl ${theme.tailwind.bgHover24} transition-colors font-medium`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={executePrint}
                        className={`px-5 py-2.5 flex items-center justify-center gap-2 ${theme.tailwind.bg18} text-white rounded-xl ${theme.tailwind.bgHover25} shadow-lg shadow-blue-500/30 transition-all hover:scale-105 font-medium`}
                      >
                        <Printer size={18} />
                        Print Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlotList;
